// src/main.js
import { createApp, watch } from 'vue'
import App from './App.vue'
import router from './router'
import store from './store'
import './assets/styles.css'
import '@fortawesome/fontawesome-free/css/all.css'
import Icon from '@/components/UI/Icon/Icon.vue'
import VueApexCharts from 'vue3-apexcharts'

// утилита для применения SEO (см. src/utils/applySeo.js)
import { applySeoSettings } from '@/utils/applySeo.js'

// простой debounce — чтобы не дергать head при каждом символе
function debounce(fn, wait = 300) {
  let t = null
  return (...args) => {
    clearTimeout(t)
    t = setTimeout(() => fn(...args), wait)
  }
}

// normalize host/domain helper
function normalizeDomain(domain = '') {
  if (!domain) return ''
  let d = String(domain).trim().toLowerCase()
  d = d.replace(/^https?:\/\//, '').replace(/^\/\//, '')
  d = d.split('/')[0]
  d = d.split(':')[0]
  return d
}

// helper: race a promise with timeout (does NOT abort underlying network but prevents long wait)
function withTimeout(promise, ms = 1500, fallback = null) {
  return Promise.race([
    promise,
    new Promise((resolve) => setTimeout(() => resolve(fallback), ms))
  ])
}

// helper: обновление favicon в <head>
function updateFavicon(url) {
  if (!url || typeof document === 'undefined') return

  let link = document.querySelector("link[rel*='icon']")
  if (!link) {
    link = document.createElement('link')
    link.rel = 'icon'
    document.head.appendChild(link)
  }

  const fullUrl = url.startsWith('http') ? url : `${window.location.origin}${url}`
  link.href = fullUrl
}

const app = createApp(App)
  .use(store)
  .use(router)
  .component('Icon', Icon)
  .use(VueApexCharts)
  .component('apexchart', VueApexCharts)

// --- client-only SEO and landing bootstrap ---
if (typeof window !== 'undefined' && window.document) {
  // apply SEO from store snapshot (if available) immediately
  try {
    const settings =
      (store.getters && store.getters['landing/landingSettings']) ||
      (store.state && store.state.landing && store.state.landing.landing_settings) ||
      {}

    applySeoSettings(settings.seo || {})

    // применяем логотип сразу при инициализации
    if (settings.logo) updateFavicon(settings.logo)
  } catch (e) {
    console.warn('applySeoSettings or initial logo apply failed', e)
  }

  // Подпишемся на изменения landing_settings используя Vue watch (надёжнее)
  const debouncedApply = debounce((seo) => {
    try {
      applySeoSettings(seo || {})
    } catch (e) {
      console.warn('applySeoSettings failed', e)
    }
  }, 300)

  try {
    // Watch для SEO
    watch(
      () =>
        (store.state && store.state.landing
          ? store.state.landing.landing_settings
          : store.getters && store.getters['landing/landingSettings']
            ? store.getters['landing/landingSettings']
            : null),
      (newSettings) => {
        const seo = (newSettings && newSettings.seo) || {}
        debouncedApply(seo)
      },
      { deep: true, immediate: false }
    )

    // Watch для логотипа
    watch(
      () => store.state?.landing?.landing_settings?.logo,
      (newLogo) => {
        if (newLogo) updateFavicon(newLogo)
      },
      { immediate: true }
    )
  } catch (e) {
    console.warn('watch setup failed', e)
  }

  // ----------------------------
  // Асинхронно подгружаем актуальные настройки с сервера по домену
  ;(async () => {
    try {
      function isUserLoggedIn() {
        try {
          if (store.getters) {
            if (store.getters['auth/isLoggedIn'] || store.getters['auth/isAuthenticated']) return true
            if (store.getters['auth/user'] || store.getters['user/current']) return true
          }
          if (store.state && store.state.auth) {
            const a = store.state.auth
            if (a.user || a.currentUser || a.loggedIn || a.isAuthenticated) return true
          }
          if (typeof window !== 'undefined' && window.localStorage) {
            if (
              window.localStorage.getItem('token') ||
              window.localStorage.getItem('auth_token') ||
              window.localStorage.getItem('access_token')
            ) return true
          }
        } catch (e) {}
        return false
      }

      let host = normalizeDomain(window.location.host || window.location.hostname || '')
      if (!host) return

      const isLocal = host.includes('localhost') || host.includes('127.0.0.1')

      // приоритет: localStorage.domain → ?domain → host
      let domainToQuery = host
      try {
        const storedDomain =
          typeof window !== 'undefined' && window.localStorage
            ? window.localStorage.getItem('domain')
            : null
        if (storedDomain) {
          domainToQuery = normalizeDomain(storedDomain)
        } else if (isLocal) {
          const q = new URLSearchParams(window.location.search).get('domain')
          domainToQuery = normalizeDomain(q)
        } else {
          domainToQuery = host
        }
      } catch (e) {
        if (isLocal) {
          const q = new URLSearchParams(window.location.search).get('domain')
          domainToQuery = normalizeDomain(q)
        } else {
          domainToQuery = host
        }
      }

      // Если юзер залогинен — используем локальные настройки
      if (isUserLoggedIn()) {
        try {
          const raw = localStorage.getItem('landing_settings')
          if (raw) {
            const parsed = JSON.parse(raw)
            store.commit('landing/SET_LANDING_SETTINGS_FROM_SERVER', parsed)
            applySeoSettings((parsed && parsed.seo) || {})
            if (parsed.logo) updateFavicon(parsed.logo)
            return
          } else {
            console.info('User logged in but no landing_settings found in localStorage — skipping network fetch.')
            return
          }
        } catch (e) {
          console.warn('Failed to parse landing_settings from localStorage, skipping fetch', e)
          return
        }
      }

      // Не залогинен — fetch с сервера
      const result = await withTimeout(
        store.dispatch('landing/fetchLandingSettingsByDomain', { domain: domainToQuery }),
        1500,
        null
      )

      if (result) {
        try {
          const updated =
            (store.getters && store.getters['landing/landingSettings']) ||
            (store.state && store.state.landing && store.state.landing.landing_settings) ||
            {}
          applySeoSettings(updated.seo || {})
          if (updated.logo) updateFavicon(updated.logo)
        } catch (e) {
          console.warn('applySeoSettings or logo apply after fetch failed', e)
        }
      }
    } catch (e) {
      console.warn('fetchLandingSettingsByDomain background error', e)
    }
  })()
}

app.mount('#app')
